import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';
import { ComplexStore } from '../stores/complex.store';
import { ApiErrorCodes } from '../constants/error-codes';
import { AppRoutes } from '../constants/routes';

const SKIP_URLS = ['/admin/auth/login', '/admin/auth/refresh', '/activation'];

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);
  const complexStore = inject(ComplexStore);
  const router = inject(Router);

  if (SKIP_URLS.some(url => req.url.includes(url))) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const authedReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authedReq).pipe(
    catchError(error => {
      if (error.status === 401) {
        const refreshToken = authService.getRefreshToken();
        if (refreshToken) {
          return authService.refresh(refreshToken).pipe(
            switchMap(tokens => {
              authService.saveTokens(tokens);
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${tokens.accessToken}` }
              });
              return next(retryReq);
            }),
            catchError(refreshError => {
              authStore.logout();
              return throwError(() => refreshError);
            })
          );
        }
        authStore.logout();
      }
      // SmartPlayr closed the management app to this venue while the session was open: every
      // admin call now answers this. The cached venue still says "open", so it is dropped first —
      // the closed screen re-reads it and would otherwise send the admin straight back.
      if (error.status === 403 && error.error?.errorCode === ApiErrorCodes.ManagementAccessDisabled) {
        complexStore.reset();
        void router.navigateByUrl(AppRoutes.AccessClosed);
      }
      return throwError(() => error);
    })
  );
};
